import React, { useEffect, useState } from "react";

const TechnoCommercialComparison = ({ questions, techWeight, commWeight, pageStatus, disabledQuotationNrs, onDownload }) => {
  const [techQuestions, setTechQuestions] = useState([]);
  const [commQuestions, setCommQuestions] = useState([]);
  const [supplierList, setSupplierList] = useState([]);

  useEffect(() => {
    if (!questions?.length) {
      setTechQuestions([]);
      setCommQuestions([]);
      setSupplierList([]);
      return;
    }
    const techSet = new Set();
    const commSet = new Set();
    const suppliersMap = new Map();

    questions.forEach((q) => {
      let hasTech = false;
      let hasComm = false;
      if (q.quotationDocument) {
        q.quotationDocument.forEach(doc => {
          const techAnswers = doc.formTECH || [];
          const commAnswers = doc.formCOMM || [];
          if (techAnswers.some(f => f.question_no === q.componentId)) hasTech = true;
          if (commAnswers.some(f => f.question_no === q.componentId)) hasComm = true;

          if (doc.quotationNr && !suppliersMap.has(doc.quotationNr)) {
            suppliersMap.set(doc.quotationNr, {
              quotationNr: doc.quotationNr,
              organisationName: doc.organisationName || ""
            });
          }
        });
      }
      if (hasTech) techSet.add(q.componentId);
      if (hasComm) commSet.add(q.componentId);
    });

    const techFiltered = questions.filter(q => techSet.has(q.componentId));
    const commFiltered = questions.filter(q => commSet.has(q.componentId));
    const finalTech = techFiltered.length ? techFiltered : questions;
    const finalComm = commFiltered.length ? commFiltered : [];
    setTechQuestions(finalTech);
    setCommQuestions(finalComm);
    setSupplierList(Array.from(suppliersMap.values()));
  }, [questions]);

  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

  // Find the supplier doc for this question + supplier
  const getDocForQuestionSupplier = (question, supplierNr) => {
    return (question?.quotationDocument || []).find(d => d.quotationNr === supplierNr);
  };

  const getSupplierAnswer = (question, supplierNr, formKey) => {
    const doc = getDocForQuestionSupplier(question, supplierNr);
    if (!doc) return "--";
    const arr = formKey === "formTECH" ? (doc.formTECH || []) : (doc.formCOMM || []);
    const response = arr.find(item => item.question_no === question.componentId);
    return response ? response.question_value : "--";
  };

  const getSupplierScore = (question, supplierNr) => {
    const doc = getDocForQuestionSupplier(question, supplierNr);
    return doc ? (Number(doc.score) || 0) : 0;
  };

  const calculateScoreBySupplier = (bandQuestions) => {
    const scoreBySupplier = {};
    bandQuestions.forEach((question) => {
      (question?.quotationDocument || []).forEach((doc) => {
        if (!doc?.quotationNr) return;
        scoreBySupplier[doc.quotationNr] = (scoreBySupplier[doc.quotationNr] || 0) + (Number(doc?.score) || 0);
      });
    });
    return scoreBySupplier;
  };

  const bandTable = (bandQuestions, bandTitle) => {
    if (!bandQuestions.length) return null;
    return (
      <div key={bandTitle} className="table-responsive">
        <table className="table table-bordered generatedTable width-150">
          <thead>
            <tr>
              <th className="tableHeader"></th>
              <th className="tableHeader"></th>
              <th className="tableHeader"></th>
              <th className="tableHeader">Weightage</th>
              {supplierList.map((supplier, index) => (
                <th className="tableHeader text-center" colSpan={2} key={index}>
                  <span className="bg-table-status bg-table-new-status px-2">
                    {supplier.organisationName} - {supplier.quotationNr}
                  </span>
                </th>
              ))}
            </tr>
            <tr>
              <th className="tableHeader _01">SL No</th>
              <th className="tableHeader _02">Questionaries</th>
              <th className="tableHeader _03">Criteria</th>
              <th className="tableHeader _04">Weightage</th>
              {supplierList.map((_supplier, sIndex) => (
                <React.Fragment key={`sub-${sIndex}`}>
                  <th className="tableHeader _04">Supplier Response</th>
                  <th className="tableHeader _05">Score</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {bandQuestions.map((question, qIndex) => {
              // Determine if tech or comm band for answer lookup
              const isTechBand = bandTitle === "Technical Evaluation";
              const formKey = isTechBand ? "formTECH" : "formCOMM";
              return (
                <tr key={qIndex}>
                  <td className="text-center">{question?.serialNo}</td>
                  <td>
                    {question?.question?.label}
                    {question.mandatory ? (
                      <span className="bg-table-status bg-table-danger-status px-2 py-1 ms-1 d-inline-block">
                        (Mandatory)
                      </span>
                    ) : (
                      <span className="bg-table-status bg-table-success-status px-2 py-1 ms-1 d-inline-block">
                        (Optional)
                      </span>
                    )}
                  </td>
                  <td>{question?.criteria}</td>
                  <td>{question?.weightage}</td>
                  {supplierList.map((supplier, sIndex) => (
                    <React.Fragment key={`${qIndex}-${sIndex}`}>
                      <td>
                        {getSupplierAnswer(question, supplier.quotationNr, formKey)}
                      </td>
                      <td>{getSupplierScore(question, supplier.quotationNr)}</td>
                    </React.Fragment>
                  ))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th className="tableHeader"></th>
              <th className="tableHeader">Total</th>
              <th className="tableHeader"></th>
              <th className="tableHeader"></th>
              {supplierList.map((supplier, index) => {
                const scoreBySupplier = calculateScoreBySupplier(bandQuestions);
                const supplierScoreTotal = scoreBySupplier[supplier.quotationNr] || 0;
                return (
                  <React.Fragment key={`total-${index}`}>
                    <th className="tableHeader"></th>
                    <th className="tableHeader">{supplierScoreTotal}</th>
                  </React.Fragment>
                );
              })}
            </tr>
            <tr>
              <th className="tableHeader"></th>
              <th className="tableHeader">Average</th>
              <th className="tableHeader"></th>
              <th className="tableHeader"></th>
              {supplierList.map((supplier, index) => {
                const scoreBySupplier = calculateScoreBySupplier(bandQuestions);
                const supplierScores = Object.values(scoreBySupplier);
                const average = supplierScores.length > 0
                  ? round2(supplierScores.reduce((a, b) => a + b, 0) / supplierScores.length)
                  : 0;
                return (
                  <React.Fragment key={`avg-${index}`}>
                    <th className="tableHeader"></th>
                    <th className="tableHeader">{average}</th>
                  </React.Fragment>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const allQuestions = [...techQuestions, ...commQuestions];
  const grandTotalBySupplier = () => {
    const techScore = calculateScoreBySupplier(techQuestions);
    const commScore = calculateScoreBySupplier(commQuestions);
    const result = {};
    supplierList.forEach(s => {
      result[s.quotationNr] = (techScore[s.quotationNr] || 0) + (commScore[s.quotationNr] || 0);
    });
    return result;
  };

  const averageAllBands = () => {
    const techScores = Object.values(calculateScoreBySupplier(techQuestions));
    const commScores = Object.values(calculateScoreBySupplier(commQuestions));
    const allScores = [...techScores, ...commScores];
    return allScores.length > 0
      ? round2(allScores.reduce((a, b) => a + b, 0) / allScores.length)
      : 0;
  };

  const technicalPointsBySupplier = () => {
    const techScore = calculateScoreBySupplier(techQuestions);
    const result = {};
    supplierList.forEach(s => {
      result[s.quotationNr] = round2((techScore[s.quotationNr] || 0) * techWeight / 100);
    });
    return result;
  };

  const isQualified = (quotationNr) => {
    return !(disabledQuotationNrs?.includes(quotationNr));
  };

  return (
    <>
      {pageStatus ? (
        <>
          {bandTable(techQuestions, "Technical Evaluation")}
          {bandTable(commQuestions, "Commercial Evaluation")}

          {allQuestions.length > 0 && (
            <div className="table-responsive mt-3">
              <table className="table table-bordered generatedTable width-150">
                <tfoot>
                  <tr>
                    <th className="tableHeader"></th>
                    <th className="tableHeader">Total Score</th>
                    <th className="tableHeader"></th>
                    <th className="tableHeader"></th>
                    {supplierList.map((supplier, index) => (
                      <React.Fragment key={`grand-total-${index}`}>
                        <th className="tableHeader"></th>
                        <th className="tableHeader">{grandTotalBySupplier()[supplier.quotationNr] || 0}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                  <tr>
                    <th className="tableHeader"></th>
                    <th className="tableHeader">Average</th>
                    <th className="tableHeader"></th>
                    <th className="tableHeader"></th>
                    {supplierList.map((supplier, index) => (
                      <React.Fragment key={`grand-avg-${index}`}>
                        <th className="tableHeader"></th>
                        <th className="tableHeader">{averageAllBands()}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                  <tr>
                    <th className="tableHeader"></th>
                    <th className="tableHeader">Technical Points ({techWeight}%)</th>
                    <th className="tableHeader"></th>
                    <th className="tableHeader"></th>
                    {supplierList.map((supplier, index) => (
                      <React.Fragment key={`tech-pts-${index}`}>
                        <th className="tableHeader"></th>
                        <th className="tableHeader">{technicalPointsBySupplier()[supplier.quotationNr] || 0}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                  <tr>
                    <th className="tableHeader"></th>
                    <th className="tableHeader">Qualification Status</th>
                    <th className="tableHeader"></th>
                    <th className="tableHeader"></th>
                    {supplierList.map((supplier, index) => (
                      <React.Fragment key={`qual-${index}`}>
                        <th className="tableHeader"></th>
                        <th className="tableHeader">
                          <span className={
                            isQualified(supplier.quotationNr)
                              ? "bg-table-status bg-table-success-status px-2 py-1 d-inline-block"
                              : "bg-table-status bg-table-danger-status px-2 py-1 d-inline-block"
                          }>
                            {isQualified(supplier.quotationNr) ? "Qualified" : "Not Qualified"}
                          </span>
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-4">Loading...</div>
      )}

      {!pageStatus && (
        <div className="text-end mt-3">
          <button className="btnTable" onClick={onDownload} title="Download Excel">
            <i className="bi bi-download"></i> Download Xl
          </button>
        </div>
      )}
    </>
  );
};

export default TechnoCommercialComparison;
